import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { supabase } from '../lib/supabase';
import CreateFirstOrgModal from './CreateFirstOrgModal';
import CreateFirstProjectModal from './CreateFirstProjectModal';

export default function InvitationChecker({ children }) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => {
    if (!user || !currentOrganization) return;

    const checkUserStatus = async () => {
      try {
        console.log('Checking user status for:', user.email);

        // First check for pending invites
        const { data: invites, error: inviteError } = await supabase
          .from('organization_invites')
          .select(`
            *,
            organizations (
              id,
              name,
              description
            )
          `)
          .eq('email', user.email)
          .is('deleted_at', null)
          .gt('expires_at', new Date().toISOString());

        console.log('Pending invites query result:', { invites, inviteError });

        if (inviteError) {
          console.error('Error checking invites:', inviteError);
        } else if (invites?.length > 0) {
          console.log('Found pending invites:', invites);
          setPendingInvites(invites);
          setShowInviteModal(true);
          return; // Exit early if we found invites
        }

        // Check memberships only if no invites found
        const { data: memberships, error: membershipError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        console.log('Memberships query result:', { memberships, membershipError });

        if (membershipError) throw membershipError;

        if (!memberships?.length) {
          console.log('No memberships found, showing create org modal');
          setShowCreateOrg(true);
        } else if (currentOrganization?.id) {
          console.log('Found memberships, checking for projects...');
          console.log('Current Organization:', currentOrganization);

          // Has organization - check for projects
          const { data: projects, error: projectError } = await supabase
            .from('projects')
            .select('id')
            .eq('organization_id', currentOrganization.id)
            .limit(1);

          console.log('Projects:', projects);

          if (projectError) throw projectError;

          if (!projects?.length) {
            console.log('No projects found, showing create project modal');
            setShowCreateProject(true);
          } else {
            console.log('Found projects, no modals needed');
          }
        }
      } catch (error) {
        console.error('Error in checkUserStatus:', error);
      }
    };

    checkUserStatus();
  }, [user, currentOrganization]);

  const handleAcceptInvite = async (invite) => {
    try {
      await supabase
        .from('organization_members')
        .insert({
          organization_id: invite.organization_id,
          user_id: user.id,
          role: invite.role
        });

      // Mark invite as used
      await supabase
        .from('organization_invites')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', invite.id);

      setShowInviteModal(false);
      window.location.reload(); // Reload to update organization context
    } catch (error) {
      console.error('Error accepting invite:', error);
    }
  };

  return (
    <>
      {showInviteModal ? (
        <div className="blur-sm transition-all">
          {children}
        </div>
      ) : (showCreateOrg || showCreateProject) ? (
        null
      ) : (
        children
      )}

      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-200 p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4 text-center">Organization Invitation</h2>
            <p className="text-gray-400 mb-8 text-center">
              You have been invited to join:
            </p>
            <div className="space-y-6 mb-8">
              {pendingInvites.map(invite => (
                <div key={invite.id} className="bg-dark-300 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-white mb-3 text-center">
                    {invite.organizations.name}
                  </h3>
                  {invite.organizations.description && (
                    <p className="text-gray-400 text-center mb-6">
                      {invite.organizations.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <button
                      onClick={() => setShowInviteModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-600 text-gray-400 rounded hover:bg-dark-400 transition-colors"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => handleAcceptInvite(invite)}
                      className="flex-1 px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary-dark transition-colors"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowInviteModal(false)}
              className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              Remind me later
            </button>
          </div>
        </div>
      )}

      {/* Create First Organization Form */}
      {showCreateOrg && !showInviteModal && (
        <div className="fixed inset-0 bg-dark-100 flex items-center justify-center z-50">
          <CreateFirstOrgModal 
            onComplete={() => {
              setShowCreateOrg(false);
              window.location.reload();
            }} 
          />
        </div>
      )}

      {/* Create First Project Form */}
      {showCreateProject && !showInviteModal && !showCreateOrg && (
        <div className="fixed inset-0 bg-dark-100 flex items-center justify-center z-50">
          <CreateFirstProjectModal 
            onComplete={() => {
              setShowCreateProject(false);
              window.location.reload();
            }} 
          />
        </div>
      )}
    </>
  );
} 